import { useQuery } from '@tanstack/react-query';
import {
  getMessageTotals,
  getModelUsage,
  getMostActiveUsers,
  getUsersWithMetrics,
  getUserModelUsageDetails,
  getMonthlyTotalCosts,
  getTTFTByModel,
  getDurationByAgent,
  getDailyMessageActivity,
  type UserMetrics,
  type BusinessUnitCostAggregate,
  type UserActivityAggregate
} from '@/api/dashboard';
import { queryKeys } from '@/lib/queryKeys';

function aggregateCostsByBusinessUnit(users: UserMetrics[]): BusinessUnitCostAggregate[] {
  const aggregateMap = new Map<string, BusinessUnitCostAggregate>();

  users.forEach((user) => {
    const businessUnit = user.business_unit?.trim() || 'Unassigned';
    const existing = aggregateMap.get(businessUnit);

    if (existing) {
      existing.cost += user.total_cost || 0;
      return;
    }

    aggregateMap.set(businessUnit, {
      unit: businessUnit,
      cost: user.total_cost || 0,
    });
  });

  return Array.from(aggregateMap.values())
    .map((item) => ({
      ...item,
      cost: Number(item.cost.toFixed(4)),
    }))
    .sort((a, b) => b.cost - a.cost);
}

function aggregateUserActivity(users: UserMetrics[], limit: number): UserActivityAggregate[] {
  const activityMap = new Map<string, number>();
  users.forEach((user) => {
    const name = user.display_name || 'Unknown';
    const messages = user.total_messages || 0;
    const existing = activityMap.get(name);
    if (existing) {
      activityMap.set(name, existing + messages);
    } else {
      activityMap.set(name, messages);
    }
  });

  return Array.from(activityMap.entries())
    .map(([name, messages]) => ({ display_name: name, message_count: messages }))
    .sort((a, b) => b.message_count - a.message_count)
    .slice(0, limit);
}

export function useMostActiveUsersQuery(
  startDate: string,
  endDate: string,
  limit: number = 5,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.mostActiveUsers(startDate, endDate, limit),
    queryFn: () => getMostActiveUsers(startDate, endDate, limit),
    enabled,
  });
}

export function useMessageTotalsQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.messageTotals(startDate, endDate),
    queryFn: () => getMessageTotals(startDate, endDate),
    enabled,
  });
}

export function useModelUsageQuery(
  startDate: string,
  endDate: string,
  limit: number = 5,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.modelUsage(startDate, endDate, limit),
    queryFn: () => getModelUsage(startDate, endDate, limit),
    enabled,
  });
}

export function useUsersWithMetricsQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.usersWithMetrics(startDate, endDate),
    queryFn: () => getUsersWithMetrics(startDate, endDate),
    enabled,
  });
}

export function useBusinessUnitCostsQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    // Same base key/query as users-with-metrics, but with select transformation.
    // TanStack reuses the cached source query and applies this derived projection.
    queryKey: queryKeys.dashboard.usersWithMetrics(startDate, endDate),
    queryFn: () => getUsersWithMetrics(startDate, endDate),
    enabled,
    select: (users: UserMetrics[]) => aggregateCostsByBusinessUnit(users),
  });
}

export function useMostActiveUsers2Query(
  startDate: string,
  endDate: string,
  limit: number = 5,
  enabled: boolean = true
) {
  return useQuery({
    // Same base key/query as users-with-metrics, but with select transformation.
    // TanStack reuses the cached source query and applies this derived projection.
    queryKey: queryKeys.dashboard.usersWithMetrics(startDate, endDate),
    queryFn: () => getUsersWithMetrics(startDate, endDate),
    enabled,
    select: (users: UserMetrics[]) => aggregateUserActivity(users, limit),
  });
}

export function useUserModelUsageDetailsQuery(
  userId: string,
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.userModelUsageDetails(userId, startDate, endDate),
    queryFn: () => getUserModelUsageDetails(userId, startDate, endDate),
    enabled,
  });
}

export function useMonthlyTotalCostsQuery(
  requestingUserId: string,
  requestingUserRole: string,
  requestingUserIsAdmin: boolean,
  numMonths: number = 12,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.monthlyTotalCosts(requestingUserId, requestingUserRole, requestingUserIsAdmin, numMonths),
    queryFn: () => getMonthlyTotalCosts(requestingUserId, requestingUserRole, requestingUserIsAdmin, numMonths),
    enabled,
  });
}

export function useTTFTByModelQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.ttftByModel(startDate, endDate),
    queryFn: () => getTTFTByModel(startDate, endDate),
    enabled,
  });
}

export function useDurationByAgentQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.durationByAgent(startDate, endDate),
    queryFn: () => getDurationByAgent(startDate, endDate),
    enabled,
  });
}

export function useDailyMessageActivityQuery(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.dashboard.dailyMessageActivity(startDate, endDate),
    queryFn: () => getDailyMessageActivity(startDate, endDate),
    enabled,
  });
}
